import { CURRENCY_SYMBOLS, DEFAULT_CURRENCY } from '../constants';
import { PricingPlan, SubscriptionStatus } from '../types';

export const formatCurrency = (
  amount: number,
  currency: string = DEFAULT_CURRENCY,
  options: {
    showSymbol?: boolean;
    showCents?: boolean;
    locale?: string;
  } = {}
): string => {
  const { showSymbol = true, showCents = true, locale = 'en-US' } = options;

  const currencyCode = currency.toUpperCase();

  // Convert from cents to dollars for most currencies
  const isZeroDecimalCurrency = ['JPY', 'KRW', 'VND', 'CLP'].includes(
    currencyCode
  );
  const displayAmount = isZeroDecimalCurrency ? amount : amount / 100;

  if (showSymbol) {
    try {
      return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency: currencyCode,
        minimumFractionDigits: showCents && !isZeroDecimalCurrency ? 2 : 0,
        maximumFractionDigits: showCents && !isZeroDecimalCurrency ? 2 : 0,
      }).format(displayAmount);
    } catch {
      // Fallback to manual formatting if Intl.NumberFormat fails
      const symbol =
        CURRENCY_SYMBOLS[currencyCode as keyof typeof CURRENCY_SYMBOLS] ||
        currencyCode;
      const formattedAmount =
        showCents && !isZeroDecimalCurrency
          ? displayAmount.toFixed(2)
          : Math.round(displayAmount).toString();
      return `${symbol}${formattedAmount}`;
    }
  }

  return showCents && !isZeroDecimalCurrency
    ? displayAmount.toFixed(2)
    : Math.round(displayAmount).toString();
};

export const formatPricingPlan = (plan: PricingPlan): string => {
  const price = formatCurrency(plan.price, plan.currency);
  const interval =
    plan.intervalCount && plan.intervalCount > 1
      ? `${plan.intervalCount} ${plan.interval}s`
      : plan.interval;

  return `${price}/${interval}`;
};

export const formatSubscriptionStatus = (
  status: SubscriptionStatus
): string => {
  const statusMap: Record<SubscriptionStatus, string> = {
    [SubscriptionStatus.ACTIVE]: 'Active',
    [SubscriptionStatus.CANCELED]: 'Canceled',
    [SubscriptionStatus.PAST_DUE]: 'Past Due',
    [SubscriptionStatus.UNPAID]: 'Unpaid',
    [SubscriptionStatus.INCOMPLETE]: 'Incomplete',
    [SubscriptionStatus.INCOMPLETE_EXPIRED]: 'Incomplete (Expired)',
    [SubscriptionStatus.TRIALING]: 'Trial',
    [SubscriptionStatus.ENDED]: 'Ended',
    [SubscriptionStatus.ALL]: 'All',
  };

  return statusMap[status] || status;
};

export const formatDate = (
  date: Date | string | number,
  options: {
    format?: 'short' | 'medium' | 'long' | 'full';
    locale?: string;
    timeZone?: string;
  } = {}
): string => {
  const { format = 'medium', locale = 'en-US', timeZone } = options;

  const dateObj =
    typeof date === 'string' || typeof date === 'number'
      ? new Date(date)
      : date;

  const formatOptions: Intl.DateTimeFormatOptions = {
    timeZone,
  };

  switch (format) {
    case 'short':
      formatOptions.dateStyle = 'short';
      break;
    case 'medium':
      formatOptions.dateStyle = 'medium';
      break;
    case 'long':
      formatOptions.dateStyle = 'long';
      break;
    case 'full':
      formatOptions.dateStyle = 'full';
      break;
  }

  try {
    return new Intl.DateTimeFormat(locale, formatOptions).format(dateObj);
  } catch {
    // Fallback to basic formatting
    return dateObj.toLocaleDateString();
  }
};

export const formatRelativeTime = (
  date: Date | string | number,
  options: {
    locale?: string;
    numeric?: 'always' | 'auto';
  } = {}
): string => {
  const { locale = 'en-US', numeric = 'auto' } = options;

  const dateObj =
    typeof date === 'string' || typeof date === 'number'
      ? new Date(date)
      : date;
  const now = new Date();
  const diffInSeconds = Math.floor((now.getTime() - dateObj.getTime()) / 1000);

  try {
    const rtf = new Intl.RelativeTimeFormat(locale, { numeric });

    if (Math.abs(diffInSeconds) < 60) {
      return rtf.format(-diffInSeconds, 'second');
    } else if (Math.abs(diffInSeconds) < 3600) {
      return rtf.format(-Math.floor(diffInSeconds / 60), 'minute');
    } else if (Math.abs(diffInSeconds) < 86400) {
      return rtf.format(-Math.floor(diffInSeconds / 3600), 'hour');
    } else if (Math.abs(diffInSeconds) < 2592000) {
      return rtf.format(-Math.floor(diffInSeconds / 86400), 'day');
    } else if (Math.abs(diffInSeconds) < 31536000) {
      return rtf.format(-Math.floor(diffInSeconds / 2592000), 'month');
    } else {
      return rtf.format(-Math.floor(diffInSeconds / 31536000), 'year');
    }
  } catch {
    // Fallback to basic relative time
    if (Math.abs(diffInSeconds) < 60) {
      return 'just now';
    } else if (Math.abs(diffInSeconds) < 3600) {
      const minutes = Math.floor(Math.abs(diffInSeconds) / 60);
      return diffInSeconds < 0
        ? `in ${minutes} minutes`
        : `${minutes} minutes ago`;
    } else if (Math.abs(diffInSeconds) < 86400) {
      const hours = Math.floor(Math.abs(diffInSeconds) / 3600);
      return diffInSeconds < 0 ? `in ${hours} hours` : `${hours} hours ago`;
    } else {
      const days = Math.floor(Math.abs(diffInSeconds) / 86400);
      return diffInSeconds < 0 ? `in ${days} days` : `${days} days ago`;
    }
  }
};

export const formatBillingInterval = (
  interval: string,
  count: number = 1
): string => {
  const intervalMap: Record<string, string> = {
    day: count === 1 ? 'daily' : `every ${count} days`,
    week: count === 1 ? 'weekly' : `every ${count} weeks`,
    month: count === 1 ? 'monthly' : `every ${count} months`,
    year: count === 1 ? 'yearly' : `every ${count} years`,
  };

  return intervalMap[interval] || interval;
};

export const formatTrialPeriod = (days: number): string => {
  if (days === 0) return 'No trial';
  if (days === 1) return '1 day trial';
  if (days < 7) return `${days} days trial`;
  if (days === 7) return '1 week trial';
  if (days < 30) return `${Math.floor(days / 7)} weeks trial`;
  if (days === 30) return '1 month trial';
  return `${Math.floor(days / 30)} months trial`;
};

export const truncateText = (text: string, maxLength: number): string => {
  if (text.length <= maxLength) return text;
  return `${text.substring(0, maxLength - 3)}...`;
};

export const formatCardBrand = (brand: string): string => {
  const brandMap: Record<string, string> = {
    visa: 'Visa',
    mastercard: 'Mastercard',
    amex: 'American Express',
    discover: 'Discover',
    jcb: 'JCB',
    diners: 'Diners Club',
    unionpay: 'UnionPay',
  };

  return brandMap[brand.toLowerCase()] || brand;
};

export const formatPaymentMethodDisplay = (paymentMethod: {
  type: string;
  card?: { brand: string; last4: string };
  bankAccount?: { last4: string; bankName?: string };
}): string => {
  if (paymentMethod.type === 'card' && paymentMethod.card) {
    return `${formatCardBrand(paymentMethod.card.brand)} •••• ${paymentMethod.card.last4}`;
  }

  if (paymentMethod.type === 'bank_account' && paymentMethod.bankAccount) {
    const bankName = paymentMethod.bankAccount.bankName || 'Bank';
    return `${bankName} •••• ${paymentMethod.bankAccount.last4}`;
  }

  return paymentMethod.type;
};

export const calculateTax = (
  amount: number,
  taxRate: number,
  options: {
    inclusive?: boolean;
    roundTo?: number;
  } = {}
): { subtotal: number; tax: number; total: number } => {
  const { inclusive = false, roundTo = 2 } = options;

  let subtotal: number;
  let tax: number;
  let total: number;

  if (inclusive) {
    // Tax is included in the amount
    total = amount;
    subtotal = amount / (1 + taxRate / 100);
    tax = amount - subtotal;
  } else {
    // Tax is added to the amount
    subtotal = amount;
    tax = amount * (taxRate / 100);
    total = amount + tax;
  }

  // Round to specified decimal places
  const multiplier = Math.pow(10, roundTo);
  return {
    subtotal: Math.round(subtotal * multiplier) / multiplier,
    tax: Math.round(tax * multiplier) / multiplier,
    total: Math.round(total * multiplier) / multiplier,
  };
};

export const formatTaxRate = (rate: number): string => {
  return `${rate.toFixed(2)}%`;
};

export const formatAmountWithTax = (
  amount: number,
  taxRate: number,
  currency: string = DEFAULT_CURRENCY,
  options: {
    inclusive?: boolean;
    showBreakdown?: boolean;
    locale?: string;
  } = {}
): string => {
  const {
    inclusive = false,
    showBreakdown = false,
    locale = 'en-US',
  } = options;
  const taxCalculation = calculateTax(amount, taxRate, { inclusive });

  if (showBreakdown) {
    const subtotalFormatted = formatCurrency(
      taxCalculation.subtotal,
      currency,
      { locale }
    );
    const taxFormatted = formatCurrency(taxCalculation.tax, currency, {
      locale,
    });
    const totalFormatted = formatCurrency(taxCalculation.total, currency, {
      locale,
    });

    return `${subtotalFormatted} + ${taxFormatted} tax = ${totalFormatted}`;
  }

  return formatCurrency(taxCalculation.total, currency, { locale });
};
